/**
 * Root component: resolves a controller, fetches the roster, persists the
 * two allowed settings, and routes to the three screens by `session.phase`
 * (BUILD-SPEC.md §7). One component tree — `size` is derived once from the
 * Teams frame context and passed straight through to Running.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { makeStyles, tokens } from "@fluentui/react-components";
import type { ControllerFactory, SessionController } from "../contract";
import type { Settings } from "../engine";
import { fetchMembers, type RosterMember } from "../roster/members";
import type { AddPersonCandidate } from "./AddPerson";
import { Finished } from "./Finished";
import { Running } from "./Running";
import { sound } from "./sound";
import { TickList } from "./TickList";
import type { ScreenSize } from "./types";

const SETTINGS_KEY = "standup-timer-settings";
export const DEFAULT_SETTINGS: Settings = { secondsPerPerson: 90, graceSeconds: 15 };

/** BUILD-SPEC.md hard rule 2: only these two values ever persist, and only
 * here — nothing else in the app writes to storage. */
export function loadSettings(): Settings {
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      secondsPerPerson: parsed.secondsPerPerson ?? DEFAULT_SETTINGS.secondsPerPerson,
      graceSeconds: parsed.graceSeconds ?? DEFAULT_SETTINGS.graceSeconds,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(settings: Settings): void {
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* storage unavailable (private browsing, etc) — settings just won't persist */
  }
}

const useStyles = makeStyles({
  root: {
    padding: tokens.spacingHorizontalM,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
});

export interface AppProps {
  controllerFactory: ControllerFactory;
}

export function App({ controllerFactory }: AppProps) {
  const styles = useStyles();
  const [controller, setController] = useState<SessionController | null>(null);
  const [, setTick] = useState(0);
  const [size, setSize] = useState<ScreenSize>("panel");
  const [settings, setSettingsState] = useState<Settings>(() => loadSettings());
  const [members, setMembers] = useState<RosterMember[]>([]);
  const [memberFetchError, setMemberFetchError] = useState<string | null>(null);

  // Resolve the controller once. ControllerFactory never rejects
  // (src/contract.ts) — failure to share is a degraded mode handled inside
  // the factory itself, not something App has to branch on.
  useEffect(() => {
    let cancelled = false;
    controllerFactory().then((c) => {
      if (!cancelled) setController(c);
    });
    return () => {
      cancelled = true;
    };
  }, [controllerFactory]);

  // Re-render on every controller state change.
  useEffect(() => {
    if (!controller) return;
    return controller.subscribe(() => setTick((t) => t + 1));
  }, [controller]);

  // Live countdown: re-render on a short interval while a turn is actually
  // running, so the displayed number keeps moving. Mirrors the Live Share
  // guidance of a ~200ms tick (BUILD-SPEC.md §5) without deriving any state
  // from the tick count itself — it only forces a re-read of controller.now().
  useEffect(() => {
    if (!controller || controller.session.phase !== "running") return;
    const id = window.setInterval(() => setTick((t) => t + 1), 200);
    return () => window.clearInterval(id);
  }, [controller, controller?.session.phase]);

  // Roster fetch (BUILD-SPEC.md §6). Every failure path resolves to
  // `ok: false` — never a throw — so this always lands in one of two
  // states: members populated, or memberFetchError set and the tick list
  // renders empty with the fallback message.
  useEffect(() => {
    let cancelled = false;
    fetchMembers().then((result) => {
      if (cancelled) return;
      if (result.ok) {
        setMembers(result.members);
        setMemberFetchError(null);
      } else {
        setMembers([]);
        setMemberFetchError(result.reason);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Screen size: the meeting stage suppresses controls, everything else
  // (side panel, details tab) gets the full panel.
  useEffect(() => {
    microsoftTeams.app
      .getContext()
      .then((ctx) => {
        setSize(ctx.page?.frameContext === "meetingStage" ? "stage" : "panel");
      })
      .catch(() => {
        /* outside Teams (dev/tests) — default to panel */
      });
  }, []);

  // Unlock the AudioContext on the first user gesture anywhere in the app
  // (BUILD-SPEC.md §7: sound defaults off and must be unlocked on click).
  useEffect(() => {
    function unlockOnce() {
      sound.unlock();
      window.removeEventListener("pointerdown", unlockOnce);
    }
    window.addEventListener("pointerdown", unlockOnce);
    return () => window.removeEventListener("pointerdown", unlockOnce);
  }, []);

  const [soundEnabled, setSoundEnabled] = useState(false);
  const handleToggleSound = useCallback((on: boolean) => {
    setSoundEnabled(on);
    sound.setEnabled(on);
  }, []);

  // Finish tone, fired exactly once per transition into "finished".
  const previousPhaseRef = useRef<string>("");
  useEffect(() => {
    if (!controller) return;
    if (controller.session.phase === "finished" && previousPhaseRef.current !== "finished") {
      sound.finish();
    }
    previousPhaseRef.current = controller.session.phase;
  }, [controller, controller?.session.phase]);

  const handleSettingsChange = useCallback((next: Settings) => {
    setSettingsState(next);
    saveSettings(next);
  }, []);

  if (!controller) {
    return <div className={styles.root}>Loading…</div>;
  }

  const { session } = controller;
  // Which screen is showing is decided purely by the shared phase, so every
  // screen agrees. "New standup" is `controller.newStandup()`, which moves
  // the shared phase back to "ticklist" for everybody at once — never a
  // local override that would leave others reading "Standup complete".
  const showTickList = session.phase === "ticklist";
  const showFinished = session.phase === "finished";

  // Deliberately NOT memoized on `controller` alone: `controller` is a
  // stable object identity for the whole session, but `session.queue`
  // changes underneath it on every action, so memoizing on `[controller,
  // members]` would go stale the moment someone is added or the standup
  // starts. This list is small — recomputing every render is cheap.
  const queuedIds = new Set(
    session.queue.map((e) => e.userId).filter((id): id is string => id !== null)
  );
  const rosterCandidates: AddPersonCandidate[] = members.filter((m) => !queuedIds.has(m.userId));

  return (
    <div className={styles.root}>
      {showTickList && (
        <TickList
          members={members}
          memberFetchError={memberFetchError}
          appOpenUsers={controller.appOpenUsers()}
          settings={settings}
          onSettingsChange={handleSettingsChange}
          onStart={(order, startSettings) => controller.start(order, startSettings)}
        />
      )}

      {(session.phase === "running" || session.phase === "paused") && (
        <Running
          session={session}
          now={controller.now()}
          size={size}
          shared={controller.shared}
          soundEnabled={soundEnabled}
          onToggleSound={handleToggleSound}
          rosterCandidates={rosterCandidates}
          onNext={(expectedIdx) => controller.next(expectedIdx)}
          onPause={() => controller.pause()}
          onResume={() => controller.resume()}
          onExtend={(seconds) => controller.extend(seconds)}
          onAddManual={(name) => controller.addManual(name)}
          onAddInvited={(userId, name) => controller.addInvited(userId, name)}
          onEnd={() => controller.end()}
          sound={sound}
        />
      )}

      {showFinished && (
        <Finished
          session={session}
          now={controller.now()}
          onNewStandup={() => controller.newStandup()}
          onResumeStandup={() => controller.resumeFromFinished()}
        />
      )}
    </div>
  );
}
